import { ref, reactive, computed } from '@vue/composition-api'
import reportsApi from '@/services/api/reports'

function buildFilterParams(filters) {
  const params = {}
  if (filters.year != null) params.year = filters.year
  if (filters.draft_id != null) params.draft_id = filters.draft_id
  if (filters.search) params.search = filters.search
  if (filters.is_paid != null) params.is_paid = filters.is_paid
  return params
}

export default function useReportsOutgoing() {
  const items = ref([])
  const totals = ref([])
  const paymentTypes = ref([])
  const statusOptions = ref([])
  const refundTypes = ref([])
  const loading = ref(false)
  const generating = ref(false)
  const currentPage = ref(1)
  const lastPage = ref(1)
  const total = ref(0)
  const queueNotEmpty = ref(false)
  const unfinalizedEvent = ref(false)

  const filters = reactive({
    year: null,
    draft_id: null,
    search: '',
    is_paid: null,
    limit: 20,
    sort: 'date',
    direction: 'desc'
  })

  const selectedIds = computed(() => items.value.filter(r => r.selected).map(r => r.id))

  async function fetchTotals() {
    try {
      const params = {}
      if (filters.year != null) params.year = filters.year
      if (filters.draft_id != null) params.draft_id = filters.draft_id
      const res = await reportsApi.getOutgoingTotals(params)
      const data = res.data.data != null ? res.data.data : {}

      // Keep the unassigned-commit ('') bucket in here so Grand Total (summed in ReportsTotals.vue)
      // includes it, matching the monolith. ReportsTotals.vue hides it from the per-type columns.
      const costs = data.totalCosts != null ? data.totalCosts : {}
      totals.value = Object.keys(costs).map(key => ({
        label: key,
        amount: parseFloat(costs[key] || 0).toFixed(2)
      }))
    } catch (_) {
      totals.value = []
    }
  }

  async function fetchGenerationStatus(draftId) {
    if (draftId == null) return
    try {
      const res = await reportsApi.generationStatus(draftId)
      const data = res.data.data != null ? res.data.data : {}
      unfinalizedEvent.value = data.unfinalizedEvent === true
      queueNotEmpty.value = data.queueNotEmpty === true
    } catch (_) { /* handled by http interceptor */ }
  }

  async function fetchOptions() {
    try {
      const res = await reportsApi.getOutgoingOptions()
      const data = res.data.data != null ? res.data.data : {}
      paymentTypes.value = data.paymentTypes != null ? data.paymentTypes : []
      statusOptions.value = data.statuses != null ? data.statuses : []
      refundTypes.value = data.refundTypes != null ? data.refundTypes : []
    } catch (_) { /* handled by http interceptor */ }
  }

  async function fetch() {
    loading.value = true
    try {
      const previouslySelected = new Set(selectedIds.value)
      const params = {
        ...buildFilterParams(filters),
        page: currentPage.value,
        limit: filters.limit
      }
      if (filters.sort) {
        params.sort = filters.sort
        params.direction = filters.direction
      }
      const res = await reportsApi.getOutgoing(params)
      const list = res.data.data != null ? res.data.data : []
      items.value = list.map(r => ({
        id: r.id,
        invoiceId: r.invoice_id,
        date: r.date_sent != null ? r.date_sent : r.created_at,
        invoiceNumber: r.invoice != null ? r.invoice.invoice_number : '',
        pdfUrl: r.file_path || null,
        canGeneratePdf: r.can_generate_pdf === true,
        competitorName: r.user != null ? `${r.user.first_name} ${r.user.last_name}` : '',
        paymentType: r.commit != null ? r.commit : '',
        type: r.type_label != null ? r.type_label : (r.type != null ? r.type : ''),
        amount: r.invoice != null ? r.invoice.total_cost : '0.00',
        status: r.is_paid ? 1 : 0,
        bank: r.bank,
        selected: previouslySelected.has(r.id)
      }))
      const meta = res.data.meta != null ? res.data.meta : {}
      currentPage.value = meta.current_page != null ? meta.current_page : 1
      lastPage.value = meta.last_page != null ? meta.last_page : 1
      total.value = meta.total != null ? meta.total : 0
    } catch (_) { // handled by http interceptor
    } finally {
      loading.value = false
    }
  }

  function setSearch(val) {
    filters.search = val
    currentPage.value = 1
    fetch()
  }

  function resetSearch() {
    filters.search = ''
    currentPage.value = 1
    fetch()
  }

  function setStatusFilter(val) {
    if (val === 'all') {
      filters.is_paid = null
    } else if (val === 'paid') {
      filters.is_paid = 1
    } else {
      filters.is_paid = 0
    }
    currentPage.value = 1
    fetch()
  }

  function setLimit(limit) {
    filters.limit = limit
    currentPage.value = 1
    fetch()
  }

  function setSort(field) {
    if (filters.sort === field) {
      filters.direction = filters.direction === 'asc' ? 'desc' : 'asc'
    } else {
      filters.sort = field
      filters.direction = 'asc'
    }
    fetch()
  }

  function setPage(page) {
    currentPage.value = page
    fetch()
  }

  function toggleSelectAll(val) {
    items.value.forEach((r) => { r.selected = val })
  }

  async function applyPaymentType(paymentType) {
    if (selectedIds.value.length === 0) return
    await reportsApi.updatePaymentType({ ids: selectedIds.value, commit: paymentType })
    await fetch()
  }

  async function applyPaymentStatus(status) {
    if (selectedIds.value.length === 0) return
    await reportsApi.updatePaymentStatus({ reports: selectedIds.value.map(id => ({ id })), status: Number(status) })
    await fetch()
  }

  async function generateReports(draftId) {
    if (draftId == null) return
    generating.value = true
    try {
      await reportsApi.generateOutgoing(draftId)
      await fetch()
      await fetchGenerationStatus(draftId)
    } catch (_) { // handled by http interceptor
    } finally {
      generating.value = false
    }
  }

  const exportingFormat = ref(null)

  async function exportReport(format) {
    if (exportingFormat.value != null) return
    exportingFormat.value = format
    try {
      const params = { ...buildFilterParams(filters), format }
      if (filters.sort) {
        params.sort = filters.sort
        params.direction = filters.direction
      }
      const res = await reportsApi.exportOutgoing(params)

      if (format === 'pdf') {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(blobUrl, '_blank')
        return
      }

      const disposition = res.headers['content-disposition']
      let filename = `reports-outgoing.${format}`
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/)
        if (match) filename = match[1]
      }
      const url = URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (_) { // handled by http interceptor
    } finally {
      exportingFormat.value = null
    }
  }

  async function applyRefundType(refundType) {
    if (!refundType || selectedIds.value.length === 0) return
    await reportsApi.updateRefunds({ report_ids: selectedIds.value, status: refundType })
    await fetch()
    await fetchTotals()
  }

  async function exportAba(apcaNumber) {
    try {
      const data = { ids: selectedIds.value, apca_number: apcaNumber }
      const res = await reportsApi.generateOutgoingAba(data)
      if (res.data != null && res.data.data != null) {
        return res.data.data
      }
    } catch (_) { // handled by http interceptor
    }
    return null
  }

  const generatingPdfIds = ref(new Set())

  async function generateInvoicePdf(item) {
    if (generatingPdfIds.value.has(item.id)) return 'pending'
    generatingPdfIds.value = new Set([...generatingPdfIds.value, item.id])
    try {
      const res = await reportsApi.getOutgoingPdf(item.id)
      const data = res.data.data != null ? res.data.data : {}
      if (data.status === 'pending' || !data.url) return 'pending'
      item.pdfUrl = data.url
      window.open(data.url, '_blank')
      return 'ready'
    } catch (_) { // handled by http interceptor
      return 'error'
    } finally {
      const next = new Set(generatingPdfIds.value)
      next.delete(item.id)
      generatingPdfIds.value = next
    }
  }

  return {
    items,
    totals,
    paymentTypes,
    statusOptions,
    refundTypes,
    loading,
    generating,
    filters,
    currentPage,
    lastPage,
    total,
    selectedIds,
    queueNotEmpty,
    unfinalizedEvent,
    fetch,
    fetchTotals,
    fetchOptions,
    fetchGenerationStatus,
    setSearch,
    resetSearch,
    setStatusFilter,
    setLimit,
    setSort,
    setPage,
    toggleSelectAll,
    applyPaymentType,
    applyPaymentStatus,
    generateReports,
    applyRefundType,
    exportReport,
    exportingFormat,
    exportAba,
    generateInvoicePdf,
    generatingPdfIds
  }
}



// WEBPACK FOOTER //
// ./src/composables/reports/useReportsOutgoing.js